import { TFunction } from 'react-i18next';
import { Nullable } from './utils';

export const COMPLIANCE_CPA_OTHER = 'other';

export const complianceServicesMap = (t: TFunction): Record<ComplianceServices, string> => ({
  tax_compliance: t('compliance:services.options.taxCompliance'),
  bookkeeping: t('compliance:services.options.bookkeeping'),
  invoicing: t('compliance:services.options.invoicing'),
});

export const complianceCPAMap = (t: TFunction): Record<string, string> => ({
  financers: t('compliance:cpa.options.financers'),
  bookmate: t('compliance:cpa.options.bookmate'),
  [COMPLIANCE_CPA_OTHER]: t('compliance:cpa.options.other'),
});

export const complianceFrecuencyMap = (t: TFunction): Record<string, string> => ({
  year: t('compliance:documents.groupingType.options.yearly'),
  month: t('compliance:documents.groupingType.options.monthly'),
});

export const compliancePlanTypeMap = (t: TFunction): Record<string, string> => ({
  invoicing: t('compliance:planType.options.invoicing'),
  total_compliance: t('compliance:planType.options.totalCompliance'),
  bookkeeping: t('compliance:planType.options.bookkeeping'),
});

export const complianceStatusMap = (t: TFunction): Record<ComplianceStatus, string> => ({
  active: t('compliance:status.active'),
  deactivated: t('compliance:status.deactivated'),
  payment_overdue: t('compliance:status.paymentOverdue'),
  not_connected: t('compliance:status.notConnected'),
});

export const enum ComplianceStatus {
  ACTIVE = 'active',
  DEACTIVATED = 'deactivated',
  PAYMENT_OVERDUE = 'payment_overdue',
  NOT_CONNECTED = 'not_connected',
}

export const enum CompliancePaymentMethod {
  STRIPE = 'stripe',
  GLOBAL_ACCOUNT = 'global_account',
}

export const enum ComplianceBillingFrequency {
  MONTHLY = 'MONTHLY',
  QUARTERLY = 'QUARTERLY',
  ANNUAL = 'ANNUAL',
}

export const complianceBillingFrequencyMap = (t: TFunction): Record<string, string> => ({
  [ComplianceBillingFrequency.MONTHLY]: t('compliance:billingFrequency.monthly'),
  [ComplianceBillingFrequency.QUARTERLY]: t('compliance:billingFrequency.quarterly'),
  [ComplianceBillingFrequency.ANNUAL]: t('compliance:billingFrequency.annual'),
});

export const complianceBillingPeriodUnitMap = (t: TFunction): Record<string, string> => ({
  [ComplianceBillingFrequency.MONTHLY]: t('compliance:billingFrequency.perMonth'),
  [ComplianceBillingFrequency.QUARTERLY]: t('compliance:billingFrequency.perQuarter'),
  [ComplianceBillingFrequency.ANNUAL]: t('compliance:billingFrequency.perYear'),
});

export type ComplianceBillingConfig = {
  fee: string;
  frequency: ComplianceBillingFrequency;
  firstChargeMonth: string;
  lastPaidAt: Nullable<string>;
  nextChargeDate: Nullable<string>;
  recurringChargeId: Nullable<string>;
};

export const complianceStripeStatusMap: Record<string, ComplianceStatus> = {
  incomplete: ComplianceStatus.PAYMENT_OVERDUE,
  incomplete_expired: ComplianceStatus.DEACTIVATED,
  trialing: ComplianceStatus.ACTIVE,
  active: ComplianceStatus.ACTIVE,
  past_due: ComplianceStatus.PAYMENT_OVERDUE,
  cancelled: ComplianceStatus.DEACTIVATED,
  unpaid: ComplianceStatus.PAYMENT_OVERDUE,
  paused: ComplianceStatus.DEACTIVATED,
};

export enum ComplianceServices {
  TAX_COMPLIANCE = 'tax_compliance',
  BOOKKEEPING = 'bookkeeping',
  INVOICING = 'invoicing',
}

export enum ComplianceRequests {
  TAX_COMPLIANCE = 'tax_compliance',
  BOOKKEEPING = 'bookkeeping',
  INVOICING = 'invoicing',
  SERVICE = 'service',
  CONTACT = 'contact',
}

export type Compliance = {
  id: number;
  company: number;
  planType?: string;
  status: string;
  includedServices?: ComplianceServices[];
  cpa?: string;
  cpaOther?: Nullable<string>;
  stripeSubscription?: Nullable<StripeSubscription>;
  connectedToStripe?: boolean;
  billingConfig?: Nullable<ComplianceBillingConfig>;
};

export type ComplianceInList = {
  id: number;
  company: number;
  companyName: string;
  companyUUID: string;
  members: number;
  status: string;
  includedServices?: ComplianceServices[];
  cpa?: { cpa: string; other: string };
  cpaOther?: Nullable<string>;
  price?: number;
  currency?: string;
};

export type ComplianceUpdate = {
  planType: string;
  includedServices: string[];
  cpa: string;
  cpaOther?: Nullable<string>;
};

export type StripeCustomer = {
  id: string;
  name: string;
};

export type StripeSubscription = {
  id: string;
  name: string;
  price?: number;
  currency?: string;
  dueDate?: string;
  frecuency?: string;
  methodAssociatedFunding?: string;
  methodAssociatedNumbers?: number;
};

export enum SectionTypes {
  YEARLY = 'yearly',
  MONTHLY = 'monthly',
  NON_RECURRING = 'no_grouping',
}

export type DocumentSection = {
  id: number;
  name: string;
  description: string;
  type: SectionTypes;
  default: boolean;
  product: ComplianceServices;
  notificationDate: Nullable<string>;
  documents: Document[];
};

export type ApiDocumentSection = {
  nameEn: string;
  nameEs: string;
  descriptionEn: string;
  descriptionEs: string;
  groupingType: SectionTypes;
  product: ComplianceServices;
};

export type Document = {
  id: number;
  year?: number;
  month?: number;
  createdAt: string;
  assetAmount: number;
  assetNames: string;
  sectionName: string;
  sectionDescription?: string;
  sectionProduct?: ComplianceServices;
};

export type ApiDocument = {
  asset: File;
  assetType: string;
  fileName: string;
  year: number;
  month: number;
};

export type ComplianceRequest = {
  id: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: Nullable<string>;
  requestType: string;
  note: Nullable<string>;
};
